// ═══════════════════════════════════════════════════════════
// Correspondence Tab — compose, send, and view messages
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { formatDateTime, formatDate as fmtDate } from '../../../../lib/format';
import {
  IconSend, IconLoader2, IconChevronDown, IconChevronUp,
  IconInbox, IconArrowNarrowRight, IconLock, IconLink, IconCheck, IconDeviceFloppy,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { SIGNATURE_BLOCK_KEY } from '../../settings/Settings';
import {
  listCorrespondenceForRequest,
  listTemplates,
  sendCorrespondence,
  applyMergeFields,
  type CorrespondenceRecord,
  type CorrTemplate,
  type CorrDirection,
  type SendCorrespondenceInput,
} from '../../../../api/correspondence';
import { createUploadToken } from '../../../../api/uploadTokens';
import { draftCorrespondence } from '../../../../api/ai';
import { updateRequest, type FoiaRequest } from '../../../../api/requests';
import type { Requester } from '../../../../api/requests';

const UPLOAD_LINK_TEMPLATE_ID   = '__upload_link__';
const ACTION_REQUEST_TEMPLATE_ID = '__action_request__';
const ACTION_REQUEST_PREFIX      = '[Action Required] ';

function draftKey(requestId: number) { return `foia:draft:${requestId}`; }

function withSignature(body: string): string {
  const sig = localStorage.getItem(SIGNATURE_BLOCK_KEY)?.trim();
  if (!sig) return body;
  return `${body}\n\n---\n${sig}`;
}

const EXPIRY_OPTIONS = [
  { days: 7,  label: '7 days' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
  { days: 60, label: '60 days' },
];

interface Props {
  requestId: number;
  request?: FoiaRequest;
  requester?: Requester | null;
  actorEmail?: string;
  jurisdictionId?: string;
  onStatusChange?: (status: string) => void;
}

const DIR_STYLE: Record<CorrDirection, { bg: string; text: string; label: string; icon: React.ReactNode }> = {
  outbound: { bg: 'var(--cds-blurple-50)', text: 'var(--color-primary-700)', label: 'Outbound', icon: <IconArrowNarrowRight size={11} /> },
  inbound:  { bg: 'var(--cds-green-50)', text: 'var(--cds-green-700)', label: 'Inbound',  icon: <IconInbox size={11} /> },
  internal: { bg: 'var(--cds-gray-50)', text: 'var(--color-neutral-500)', label: 'Internal', icon: <IconLock size={11} /> },
};

function formatDate(iso?: string) {
  return formatDateTime(iso);
}

function CorrespondenceItem({ record }: { record: CorrespondenceRecord }) {
  const [expanded, setExpanded] = useState(false);
  const isActionRequest = record.subject?.startsWith(ACTION_REQUEST_PREFIX);
  const s = DIR_STYLE[record.direction];
  const displaySubject = isActionRequest
    ? record.subject.slice(ACTION_REQUEST_PREFIX.length)
    : record.subject;

  return (
    <div style={{
      border: isActionRequest ? '1px solid #FCD8A3' : '1px solid var(--color-neutral-200)',
      borderRadius: 4, marginBottom: 8, overflow: 'hidden',
      background: isActionRequest ? 'var(--cds-yellow-50)' : 'white',
    }}>
      <button
        onClick={() => setExpanded(x => !x)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {isActionRequest ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '1px 8px', borderRadius: 99, fontSize: 12, fontWeight: 600,
            background: '#FEF3C7', color: 'var(--cds-yellow-800)', flexShrink: 0,
          }}>
            <IconAlertTriangle size={10} /> Action Required
          </span>
        ) : (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '1px 8px', borderRadius: 99, fontSize: 12, fontWeight: 600,
            background: s.bg, color: s.text, flexShrink: 0,
          }}>
            {s.icon} {s.label}
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displaySubject || '(no subject)'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 1 }}>
            {record.toAddresses?.join(', ')} · {formatDate(record.sentAt)}
            {record.isAutomated && <span style={{ marginLeft: 8, color: 'var(--color-neutral-300)' }}>automated</span>}
          </div>
        </div>
        {expanded ? <IconChevronUp size={14} style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }} />
                  : <IconChevronDown size={14} style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }} />}
      </button>
      {expanded && (
        <div style={{
          padding: '12px 14px',
          borderTop: '1px solid var(--color-neutral-100)',
          fontSize: 14, color: 'var(--color-neutral-700)', lineHeight: 1.7,
          whiteSpace: 'pre-wrap', fontFamily: 'inherit',
          background: '#FAFAFA',
        }}>
          {record.body}
        </div>
      )}
    </div>
  );
}

export default function CorrespondenceTab({ requestId, request, requester, actorEmail, jurisdictionId, onStatusChange }: Props) {
  const [records, setRecords]   = useState<CorrespondenceRecord[]>([]);
  const [templates, setTemplates] = useState<CorrTemplate[]>([]);
  const [loading, setLoading]   = useState(true);
  const [composing, setComposing] = useState(false);
  const [sending, setSending]   = useState(false);

  const [form, setForm] = useState({
    direction: 'outbound' as CorrDirection,
    to: requester?.email ?? '',
    subject: '',
    body: '',
    templateId: '' as string | number,
  });

  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [hasDraft, setHasDraft]     = useState(false);

  // Upload link sub-form state
  const [linkInstructions, setLinkInstructions] = useState('');
  const [linkExpiryDays, setLinkExpiryDays]     = useState(30);
  const [linkGenerating, setLinkGenerating]     = useState(false);
  const [linkGenerated, setLinkGenerated]       = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      listCorrespondenceForRequest(requestId),
      listTemplates(jurisdictionId ?? '1'),
    ]).then(([recs, tmpl]) => {
      setRecords(recs);
      setTemplates(tmpl);
    }).finally(() => setLoading(false));
  }, [requestId, jurisdictionId]);

  // Auto-refresh when realtime data change fires for correspondence table
  useEffect(() => {
    function handleDataChange(e: Event) {
      const tables: string[] = (e as CustomEvent).detail?.tables ?? [];
      if (tables.includes('z51jczl9a')) {
        listCorrespondenceForRequest(requestId).then(setRecords).catch(() => {});
      }
    }
    window.addEventListener('foia:datachange', handleDataChange);
    return () => window.removeEventListener('foia:datachange', handleDataChange);
  }, [requestId]);

  // When requester email becomes known, pre-fill To field
  useEffect(() => {
    if (requester?.email && !form.to) {
      setForm(f => ({ ...f, to: requester.email ?? '' }));
    }
  }, [requester]);

  // Check if a draft exists for this request
  useEffect(() => {
    setHasDraft(!!localStorage.getItem(draftKey(requestId)));
  }, [requestId]);

  function applyTemplate(tmpl: CorrTemplate) {
    const ctx = {
      requestNumber:  request?.requestNumber,
      subject:        request?.subject,
      requesterName:  requester?.displayName,
      requesterEmail: requester?.email,
      deadline:       fmtDate(request?.effectiveDeadline ?? request?.statutoryDeadline),
      daysRemaining:  request?.daysRemaining,
      department:     request?.assignedDeptId,
      assignee:       request?.assignedUserId,
    };
    setForm(f => ({
      ...f,
      templateId: tmpl.recordId,
      subject: applyMergeFields(tmpl.subject, ctx),
      body: withSignature(applyMergeFields(tmpl.body, ctx)),
    }));
    setTimeout(() => bodyRef.current?.focus(), 50);
  }

  async function handleGenerateUploadLink() {
    if (!request) return;
    setLinkGenerating(true);
    try {
      const t = await createUploadToken({
        requestId:   requestId,
        label:       linkInstructions.trim(),
        expiryDays:  linkExpiryDays,
        createdBy:   actorEmail ?? '',
      });
      const url = `${window.location.origin}/upload/${t.token}`;
      const reqNum = request.requestNumber;
      const subject = `Documents Needed — Request ${reqNum}`;
      const body = [
        `Please use the secure link below to upload documents related to public records request ${reqNum}:`,
        '',
        request.subject ? `Request: ${request.subject}` : '',
        '',
        linkInstructions.trim() ? `Instructions:\n${linkInstructions.trim()}` : '',
        '',
        `Upload link (no login required):\n${url}`,
        '',
        `This link expires in ${linkExpiryDays} days. If you have questions, please reply to this email.`,
      ].filter(line => line !== undefined).join('\n').replace(/\n{3,}/g, '\n\n').trim();

      setForm(f => ({ ...f, subject, body, direction: 'outbound', to: f.to }));
      setLinkGenerated(true);
      setTimeout(() => bodyRef.current?.focus(), 50);
    } finally {
      setLinkGenerating(false);
    }
  }

  function handleSaveDraft() {
    localStorage.setItem(draftKey(requestId), JSON.stringify(form));
    setHasDraft(true);
  }

  function handleLoadDraft() {
    const raw = localStorage.getItem(draftKey(requestId));
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      setForm(f => ({ ...f, ...saved }));
      setComposing(true);
    } catch { /* ignore */ }
  }

  function handleDiscardDraft() {
    localStorage.removeItem(draftKey(requestId));
    setHasDraft(false);
  }

  async function handleSend() {
    if (!form.subject.trim() || !form.body.trim()) return;
    setSending(true);
    const isActionRequest = form.templateId === ACTION_REQUEST_TEMPLATE_ID;
    try {
      const input: SendCorrespondenceInput = {
        requestId,
        direction: isActionRequest ? 'outbound' : form.direction,
        subject: isActionRequest
          ? ACTION_REQUEST_PREFIX + form.subject.trim()
          : form.subject.trim(),
        body: form.body.trim(),
        templateId: (form.templateId && form.templateId !== ACTION_REQUEST_TEMPLATE_ID)
          ? Number(form.templateId) : undefined,
        isAutomated: false,
      };
      if (input.direction !== 'internal') {
        input.toAddresses = form.to.split(',').map(s => s.trim()).filter(Boolean);
      }
      const sent = await sendCorrespondence(input, actorEmail);
      setRecords(r => [sent, ...r]);

      // Flip request status to awaitingRequester when sending an action request
      if (isActionRequest && request) {
        await updateRequest(request.recordId, { status: 'awaitingRequester' }, actorEmail);
        onStatusChange?.('awaitingRequester');
      }

      setComposing(false);
      setForm(f => ({ ...f, subject: '', body: '', templateId: '' }));
      handleDiscardDraft();
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-400)' }}>
        <IconLoader2 size={22} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: hasDraft && !composing ? 8 : 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-700)' }}>
          {records.length} message{records.length !== 1 ? 's' : ''}
        </span>
        <button
          className="btn btn-primary btn-sm"
          style={{ gap: 4 }}
          onClick={() => { setComposing(true); setLinkGenerated(false); setLinkInstructions(''); setLinkExpiryDays(30); }}
        >
          <IconSend size={12} /> Compose
        </button>
      </div>

      {/* Draft resume banner */}
      {hasDraft && !composing && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
          background: 'var(--cds-yellow-50)', border: '1px solid #FCD8A3', borderRadius: 7,
          padding: '9px 14px', fontSize: 12, color: 'var(--cds-yellow-800)',
        }}>
          <IconDeviceFloppy size={14} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>You have an unsent draft for this request.</span>
          <button className="btn btn-secondary btn-sm" style={{ fontSize: 12 }} onClick={handleLoadDraft}>Resume Draft</button>
          <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, color: 'var(--cds-red-700)' }} onClick={handleDiscardDraft}>Discard</button>
        </div>
      )}

      {/* Compose panel */}
      {composing && (
        <div style={{
          background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-200)',
          borderRadius: 4, padding: '14px 16px', marginBottom: 16,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Direction */}
            <div style={{ flex: '0 0 140px' }}>
              <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Direction</label>
              <select
                className="form-input"
                style={{ fontSize: 12 }}
                value={form.direction}
                onChange={e => setForm(f => ({ ...f, direction: e.target.value as CorrDirection }))}
              >
                <option value="outbound">Outbound (to requester)</option>
                <option value="inbound">Inbound (log receipt)</option>
                <option value="internal">Internal note</option>
              </select>
            </div>
            {/* Template picker */}
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Use Template</label>
              <select
                className="form-input"
                style={{ fontSize: 12 }}
                value={form.templateId}
                onChange={e => {
                  const val = e.target.value;
                  if (val === ACTION_REQUEST_TEMPLATE_ID) {
                    setForm(f => ({ ...f, templateId: ACTION_REQUEST_TEMPLATE_ID, direction: 'outbound', subject: '', body: '' }));
                  } else if (val === UPLOAD_LINK_TEMPLATE_ID) {
                    setForm(f => ({ ...f, templateId: UPLOAD_LINK_TEMPLATE_ID, direction: 'outbound' }));
                    setLinkGenerated(false);
                  } else {
                    const t = templates.find(t => t.recordId === Number(val));
                    if (t) applyTemplate(t);
                    else setForm(f => ({ ...f, templateId: '' }));
                  }
                }}
              >
                <option value="">— Select template —</option>
                <option value={ACTION_REQUEST_TEMPLATE_ID}>⚠ Request Action / Clarification from Requester</option>
                <option value={UPLOAD_LINK_TEMPLATE_ID}>📎 Request Documents via Upload Link</option>
                {templates.length > 0 && <option disabled>──────────────</option>}
                {templates.map(t => (
                  <option key={t.recordId} value={t.recordId}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Action request notice */}
          {form.templateId === ACTION_REQUEST_TEMPLATE_ID && (
            <div style={{
              background: 'var(--cds-yellow-50)', border: '1px solid #FCD8A3', borderRadius: 4,
              padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'flex-start',
            }}>
              <IconAlertTriangle size={14} style={{ color: 'var(--cds-yellow-600)', marginTop: 1, flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: 'var(--cds-yellow-800)', lineHeight: 1.6 }}>
                <strong>Action Required — Requester Must Respond</strong><br />
                This message will be shown prominently in the requester portal as a pending action.
                The request status will automatically change to <em>Awaiting Requester</em> when sent.
                Use the subject and body below to describe exactly what you need from the requester.
              </div>
            </div>
          )}

          {/* Upload link sub-form */}
          {form.templateId === UPLOAD_LINK_TEMPLATE_ID && !linkGenerated && (
            <div style={{
              background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 4,
              padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6D28D9', display: 'flex', alignItems: 'center', gap: 5 }}>
                <IconLink size={13} /> Upload Link Settings
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--cds-violet-700)', display: 'block', marginBottom: 3 }}>Instructions for uploader <span style={{ fontWeight: 400, color: '#A78BFA' }}>(optional)</span></label>
                <textarea
                  className="form-input"
                  style={{ fontSize: 12, minHeight: 60, resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
                  placeholder="e.g. Please upload all budget documents from Q1 2024, including any supporting spreadsheets."
                  value={linkInstructions}
                  onChange={e => setLinkInstructions(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  className="form-input"
                  style={{ fontSize: 12, padding: '6px 10px' }}
                  value={linkExpiryDays}
                  onChange={e => setLinkExpiryDays(Number(e.target.value))}
                >
                  {EXPIRY_OPTIONS.map(o => (
                    <option key={o.days} value={o.days}>Expires in {o.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ gap: 5, whiteSpace: 'nowrap' }}
                  onClick={handleGenerateUploadLink}
                  disabled={linkGenerating}
                >
                  {linkGenerating
                    ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    : <IconLink size={12} />
                  }
                  Generate & Insert Link
                </button>
              </div>
            </div>
          )}

          {form.templateId === UPLOAD_LINK_TEMPLATE_ID && linkGenerated && (
            <div style={{ background: 'var(--cds-green-50)', border: '1px solid var(--cds-green-200)', borderRadius: 4, padding: '8px 12px', fontSize: 12, color: 'var(--cds-green-700)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <IconCheck size={13} /> Upload link generated and inserted into the message body.
            </div>
          )}

          {/* To field (not for internal) */}
          {form.direction !== 'internal' && (
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>To (comma-separated emails)</label>
              <input
                className="form-input"
                style={{ fontSize: 12 }}
                placeholder="requester@email.com"
                value={form.to}
                onChange={e => setForm(f => ({ ...f, to: e.target.value }))}
              />
            </div>
          )}

          {/* Subject */}
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Subject</label>
            <input
              className="form-input"
              style={{ fontSize: 14 }}
              placeholder="Subject *"
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
            />
          </div>

          {/* Body */}
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Message *</label>
            <textarea
              ref={bodyRef}
              className="form-input"
              style={{ fontSize: 14, resize: 'vertical', minHeight: 160, fontFamily: 'inherit', lineHeight: 1.6 }}
              placeholder="Write your message…"
              value={form.body}
              onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setComposing(false); setLinkGenerated(false); setLinkInstructions(''); setForm(f => ({ ...f, templateId: '', subject: '', body: '' })); }}>Cancel</button>
            <button
              className="btn btn-secondary btn-sm"
              style={{ gap: 4 }}
              disabled={!form.subject.trim() && !form.body.trim()}
              onClick={handleSaveDraft}
            >
              <IconDeviceFloppy size={11} /> Save Draft
            </button>
            <button
              className="btn btn-secondary btn-sm"
              style={{ gap: 4 }}
              disabled={aiDrafting}
              onClick={async () => {
                if (!request) return;
                setAiDrafting(true);
                try {
                  const typeMap: Record<CorrDirection, 'acknowledgment' | 'clarification' | 'extension'> = {
                    outbound: 'acknowledgment', inbound: 'acknowledgment', internal: 'clarification',
                  };
                  const drafted = await draftCorrespondence({
                    type: typeMap[form.direction],
                    requestNumber: request.requestNumber,
                    requesterName: requester?.displayName ?? 'Requester',
                    subject: request.subject,
                    jurisdiction: '',
                    deadline: request.effectiveDeadline ? fmtDate(request.effectiveDeadline) : undefined,
                  });
                  setForm(f => ({ ...f, subject: drafted.subject, body: withSignature(drafted.body) }));
                } finally {
                  setAiDrafting(false);
                }
              }}
            >
              {aiDrafting
                ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                : '✦ AI Draft'
              }
            </button>
            <button
              className="btn btn-primary btn-sm"
              style={{ gap: 4 }}
              disabled={!form.subject.trim() || !form.body.trim() || sending}
              onClick={handleSend}
            >
              {sending
                ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                : <><IconSend size={11} /> {form.direction === 'internal' ? 'Save Note' : 'Send'}</>
              }
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {records.length === 0 && !composing && (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-neutral-400)', fontSize: 14 }}>
          No correspondence yet. Click Compose to send a message.
        </div>
      )}

      {/* Thread */}
      {records.map(r => <CorrespondenceItem key={r.recordId} record={r} />)}
    </div>
  );
}
